import { useStaffApi } from "../../context/ApiContext.tsx";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Appraisal } from "../../api/ApiTypes.ts";
import { Search } from "lucide-react";

export const StaffDashboard = () => {
    const staffApi = useStaffApi();
    const navigate = useNavigate();
    const [orders, setOrders] = useState<Appraisal[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchId, setSearchId] = useState('');

    const refreshOrders = async () => {
        try {
            setLoading(true);
            const data = await staffApi.getDashboard();
            setOrders(data);
        } catch (error) {
            console.error('Failed to load orders:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        refreshOrders();
    }, []);

    const handleRowClick = (orderId: number) => {
        navigate(`/staff/orderdetails/${orderId}`);
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = searchId.trim();
        if (trimmed) {
            navigate(`/staff/orderdetails/${trimmed}`);
        }
    };

    return (
        <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-6">
            {/* Header section */}
            <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4">
                <h1 className="text-2xl font-bold text-slate-800">Staff Dashboard</h1>

                <form onSubmit={handleSearch} className="flex gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:flex-initial">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by Order ID"
                            value={searchId}
                            onChange={(e) => setSearchId(e.target.value)}
                            className="w-full sm:w-64 pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <button
                        type="submit"
                        className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1 sm:gap-2 hover:cursor-pointer"
                    >
                        <Search className="h-4 w-4" />
                        <span>Search</span>
                    </button>
                </form>
            </div>

            {loading ? (
                <div className="text-gray-500">Loading orders...</div>
            ) : (
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                File Number
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Order ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Property Address
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {orders.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="px-6 py-4 text-center text-gray-500">
                                    No orders found
                                </td>
                            </tr>
                        ) : (
                            orders.map((order) => (
                                <tr
                                    key={order.id}
                                    className="hover:bg-gray-50 cursor-pointer"
                                    onClick={() => handleRowClick(order.id)}
                                >
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {order.usaFileNumber || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {order.id}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        {[
                                            order.propertyAddressLine1,
                                            order.propertyAddressLine2,
                                            order.propertyCity,
                                            order.propertyStateCode,
                                            order.propertyZip
                                        ].filter(Boolean).join(', ')}
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
